import { NavLink } from "react-router-dom";

import {
  Badge,
  Link,
  OverflowText,
  PlaceholderProducts,
} from "@transfr-inc/dashboard-components";
import {
  BasicCell,
  Column,
  ColumnOptions,
  ColumnType,
  DataTable,
  SortDirection,
  TableCellListContent,
} from "@transfr-inc/dashboard-components/data-table";
import { formatDateDigits } from "@transfr-inc/dashboard-components/utils";

import { ArchivedBadge, DemoBadge } from "@components/custom/content";
import { createClassroomDetailLink } from "@lib/utils/navigation.utils";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

export function ClassroomsTable({
  data,
  responsiveStyles,
  onRowSelectionChange,
  selectionEnabled,
  bottomBarOptions,
  missionControlLiveCount,
  isIntegrationOrgCode = () => false,
}) {
  const columns = [
    ...(selectionEnabled
      ? [new Column("select", /*title:*/ undefined, ColumnType.SELECTION)]
      : []),
    new Column(
      "name",
      "Name",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        sortKeyFields: ["name"],
        customFunction: (value, row) => {
          const mcLiveCount = missionControlLiveCount[row.classroomId] || 0;

          return (
            <div className="classroom-name-cell">
              <OverflowText text={value}>
                <Link
                  routerComponent={NavLink}
                  to={createClassroomDetailLink({
                    classroomId: row.classroomId,
                    search: {
                      from: "All Classrooms",
                    },
                  })}
                >
                  {value}
                </Link>
              </OverflowText>
              {mcLiveCount > 0 && (
                <Badge className="badge" value={`${mcLiveCount} LIVE`} />
              )}
              {row.isDemo && <DemoBadge />}
              {row.isArchived && <ArchivedBadge />}
            </div>
          );
        },
      })
    ),
    new Column(
      "organizationName",
      "organization",
      ColumnType.BASIC,
      new ColumnOptions({ sortingEnabled: true, overflowEnabled: true })
    ),
    new Column(
      "totalInstructors",
      "Instructors",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, row) => {
          const renderedNames = [];

          if (row.instructors) {
            row.instructors.map((user) => {
              renderedNames.push(`${user.lastName}, ${user.firstName} `);
            });
          }
          return (
            <TableCellListContent
              collection={renderedNames}
              collectionCount={row.totalInstructors}
            />
          );
        },
        sortKeyFields: ["instructorSortKey"],
      })
    ),
    new Column(
      "totalTrainees",
      `${getLearnerTerm({ plural: true })}`,
      ColumnType.COUNT,
      new ColumnOptions({ sortingEnabled: true })
    ),
    new Column(
      "created",
      "Date Created",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value) => formatDateDigits(value, true),
      })
    ),
    new Column(
      "lastActivityDate",
      "Last Activity",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value) => (
          <BasicCell
            value={
              value &&
              new Date(value).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            }
          />
        ),
      })
    ),
    new Column(
      "code",
      "Code",
      ColumnType.BASIC,
      new ColumnOptions({ sortingEnabled: true, overflowEnabled: true })
    ),
    new Column(
      "products",
      "Products",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, row) => (
          <PlaceholderProducts
            isUnifiedTrek
            products={value}
            isProfessionalLearning={row?.isProfessionalLearning}
          />
        ),
      })
    ),
  ];

  return (
    <DataTable
      columns={columns}
      data={data ?? []}
      className="classroom-list-data-table"
      keyFields={["classroomId"]}
      defaultSortPropName="name"
      defaultSortDirection={SortDirection.ASCENDING}
      responsiveStyles={responsiveStyles}
      keyPrefix="classroom"
      onRowSelectionChange={onRowSelectionChange}
      bottomBarOptions={bottomBarOptions}
      tableContentName={{ singular: "classroom", plural: "classrooms" }}
      rowHeightConfig={{ large: 54, medium: 150, small: 200 }}
      isRowDisabled={(row) => row.isArchived}
      isRowLocked={(row) => isIntegrationOrgCode(row.organizationCode)}
      virtualRowEnabled
    />
  );
}
